import { Lock } from 'lucide-react'
import { useEffect, useState } from 'react'
import {
  useNotificationThresholds,
  useSaveNotificationThresholds,
} from '@/api/hooks/use-notifications'

function Toggle({
  checked,
  onChange,
}: {
  checked: boolean
  onChange: (v: boolean) => void
}): React.JSX.Element {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${checked ? 'bg-brand-500' : 'bg-surface-3'}`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${checked ? 'translate-x-6' : 'translate-x-1'}`}
      />
    </button>
  )
}

interface AlertRule {
  key: string
  label: string
  enabled: boolean
  threshold: number | null
  thresholdSuffix?: string
  growthOnly?: boolean
}

const DEFAULT_ALERTS: AlertRule[] = [
  {
    key: 'bounce_warning',
    label: 'Bounce rate warning',
    enabled: true,
    threshold: 3,
    thresholdSuffix: '%',
  },
  {
    key: 'bounce_critical',
    label: 'Bounce rate critical',
    enabled: true,
    threshold: 5,
    thresholdSuffix: '%',
  },
  {
    key: 'complaint_warning',
    label: 'Complaint warning',
    enabled: true,
    threshold: 0.1,
    thresholdSuffix: '%',
  },
  {
    key: 'complaint_critical',
    label: 'Complaint critical',
    enabled: true,
    threshold: 0.3,
    thresholdSuffix: '%',
  },
  {
    key: 'domain_verification_failed',
    label: 'Domain verification failed',
    enabled: true,
    threshold: null,
  },
  {
    key: 'ip_warming_paused',
    label: 'IP warming paused',
    enabled: false,
    threshold: null,
    growthOnly: true,
  },
  {
    key: 'ip_warming_complete',
    label: 'IP warming complete',
    enabled: false,
    threshold: null,
    growthOnly: true,
  },
  { key: 'account_paused', label: 'Account paused', enabled: true, threshold: null },
  { key: 'daily_summary', label: 'Daily summary', enabled: false, threshold: null },
  { key: 'weekly_report', label: 'Weekly report', enabled: true, threshold: null },
]

export function AlertRulesSection(): React.JSX.Element {
  const { data: thresholdsData } = useNotificationThresholds()
  const saveThresholds = useSaveNotificationThresholds()

  const [alerts, setAlerts] = useState<AlertRule[]>(DEFAULT_ALERTS)
  const [didInit, setDidInit] = useState(false)
  useEffect(() => {
    if (thresholdsData?.data && !didInit) {
      setAlerts((prev) =>
        prev.map((alert) => {
          const remote = thresholdsData.data.find((t) => t.metric === alert.key)
          if (!remote) return alert
          return { ...alert, enabled: remote.enabled, threshold: remote.value }
        }),
      )
      setDidInit(true)
    }
  }, [thresholdsData, didInit])

  function toggleAlert(key: string): void {
    setAlerts((prev) => prev.map((a) => (a.key === key ? { ...a, enabled: !a.enabled } : a)))
  }

  function updateThreshold(key: string, value: string): void {
    const num = Number.parseFloat(value)
    setAlerts((prev) =>
      prev.map((a) =>
        a.key === key ? { ...a, threshold: Number.isNaN(num) ? a.threshold : num } : a,
      ),
    )
  }

  function handleSaveThresholds(): void {
    saveThresholds.mutate({
      thresholds: alerts
        .filter((a) => a.threshold !== null)
        .map((a) => ({
          metric: a.key,
          operator: 'gte' as const,
          value: a.threshold ?? 0,
          enabled: a.enabled,
        })),
    })
  }

  return (
    <div className="rounded-xl border border-surface-3 bg-surface-0 p-6">
      <h2 className="text-lg font-semibold text-text-primary">Alert Rules</h2>
      <p className="mt-1 text-sm text-text-secondary">
        Choose which alerts to enable and set thresholds.
      </p>

      <div className="mt-4 divide-y divide-surface-3">
        {alerts.map((alert) => (
          <div key={alert.key} className="flex items-center justify-between py-3">
            <div className="flex items-center gap-2">
              <span className="text-sm text-text-primary">{alert.label}</span>
              {alert.growthOnly && (
                <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700">
                  <Lock className="h-3 w-3" />
                  Growth+
                </span>
              )}
            </div>
            <div className="flex items-center gap-3">
              {alert.threshold !== null && (
                <div className="flex items-center gap-1">
                  <input
                    type="number"
                    value={alert.threshold}
                    onChange={(e) => updateThreshold(alert.key, e.target.value)}
                    step={alert.threshold < 1 ? 0.1 : 1}
                    min={0}
                    className="w-16 rounded-md border border-surface-3 px-2 py-1 text-right text-sm focus:border-brand-500 focus:outline-none"
                  />
                  <span className="text-sm text-text-secondary">{alert.thresholdSuffix}</span>
                </div>
              )}
              <Toggle checked={alert.enabled} onChange={() => toggleAlert(alert.key)} />
            </div>
          </div>
        ))}
      </div>

      <div className="mt-4">
        <button
          type="button"
          onClick={handleSaveThresholds}
          disabled={saveThresholds.isPending}
          className="rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600 disabled:opacity-50"
        >
          {saveThresholds.isPending ? 'Saving...' : 'Save Thresholds'}
        </button>
        {saveThresholds.isSuccess && (
          <span className="ml-3 text-sm font-medium text-green-700">Saved!</span>
        )}
        {saveThresholds.isError && (
          <span className="ml-3 text-sm font-medium text-red-700">Failed to save.</span>
        )}
      </div>
    </div>
  )
}
